/* eslint-disable pii/no-phone-number */

import { Media } from './medias/medias.models';
import { ProductState } from './product-status.service';

import {
    ApiCertifiedMaterials,
    ApiProductCategory,
    ApiProductFamilyInfo,
    ApiProductPromotions,
    ApiProductSubfamilyInfo,
    ApiProductXmedia,
    ApiUnitPrice,
} from '@api/_api/api-catalog/api-products/api-products.models';
import { ProductsArrayPublicModels } from '@features/productsArray';
import { ImageDataMedia } from '@shared/product/models';

// eslint-disable-next-line no-secrets/no-secrets
/**
 * Resumen de conceptos y casuísticas del API Rest:
 * - MOCACO (Modelo Calidad Color):
 *   Un producto que es igual a otro (aka producto padre) pero cuyo primer color es distinto
 *   al del producto padre. Usado para mostrar varias veces un mismo producto en parrilla
 *   pero con colores distintos.
 *
 * - ESTILISMO:
 *   Es un MOCACO cuyo set de imágenes son distintas a las del producto padre.
 *   Usado para mostrar varias veces un mismo producto en parrilla pero
 *   con fotos distintas (distinto fondo por ejemplo).
 *   También se puede usar para mostrar un producto con una imagen
 *   en una categoría y con otra imagen en otra categoría.
 *
 * - BUNDLE:
 *   Tipo especial de producto que representa a un conjunto de productos. Un ejemplo de uso
 *   podría ser un pijama que tiene parte de arriba y parte de abajo y se pueden vender
 *   por separado, sin embargo se quieren mostrar en parrilla como un único producto (agrupados).
 *
 *  Nosotros trabajaremos con un modelo común para todos los tipos de productos.
 *  Así que para los MOCACOS/ESTILISMOS se hacen las transformaciones de datos necesarias
 *  para que se puedan tratar como un producto "normal".
 *  El campo bundleProducts solo se devolverá cubierto con productos en el caso de el producto
 *  sea de tipo bundle (gridElemType='BUNDLE').
 *  En el resto de casos (productos normales, mocacos, estilismos...) el campo bundleProducts devolverá
 *  un array vacío.
 *  Todas estas transformaciones se implementarán en la función `convertApiProductIfNeccesary` del
 *  transformer.
 *
 * Ref: https://axinic.central.inditex.grp/confluence/pages/viewpage.action?pageId=341483406
 */
export interface Product {
    mocaId: string;
    moca: string;
    mocaAttributes?: ProductAttribute[];
    attributes: ProductAttribute[] /** Listado de atributos. */;
    availabilityDate: string /* Fecha disponible del producto */;
    colors: ProductColor[] /** Listado de colores: para cada color hay un conjunto de tallas y medias */;
    id: string /** Identificador */; // FIXME: El id de producto es un number
    composition: Composition[];
    certifiedMaterials: ApiCertifiedMaterials;
    care: GenericDetailItem[];
    defaultImageType: string;
    longDescription?: string /** Descripción larga */;
    description?: string /** Descripción corta */;
    detail?: ProductDetail;
    name: string /** Nombre */;
    priceInfo?: ProductPrice /** El modelo consta de los valores relacionados con el precio */;
    discountsPercentages?: DiscountsPercentages;
    type: ProductType /* Indica si es un producto o un bundle de productos */;
    gridElemType: ProductGridElementType /* Indica el tipo de producto a nivel de parrilla */;
    bundleProducts: Product[] /** Lista de productos de un bundle */;
    bundleReference?: string;
    // eslint-disable-next-line max-len
    metadata: ProductsArrayPublicModels.PublicProductMetadata /** Metadatos de un producto: info extra útil para los servicios cocinados */;
    mainColorid: string;
    status: ProductState; // generar modelo de state del producto, tendra talla seleccionada y color seleccionado
    customization?: Customization;
    visibilityValue: ProductAvailabilityType;
    reference: string;
    displayReference: string;
    locationPath: string /** variable para saber la url del producto */;
    relatedType?: string;
    xmedia?: ApiProductXmedia[] | null;
    xmediaDefaultSet?: number;
    key?: string;
    nameEn?: string;
    subFamilyNameEn?: string;
    subFamily?: string;
    section?: string;
    sectionNameEn?: string;
    family?: string;
    familyNameEn?: string;
    familyCode?: string;
    ingredients?: GenericDetailItem[];
    warnings?: GenericDetailItem[];
    isVirtualCard: boolean;
    isPhysicalCard: boolean;
    imageData?: ImageDataMedia;
    productType?: string;
    perfum?: boolean;
    relatedCategories?: ApiProductCategory[] | null;
    bannerLinks?: BannerLink[];
    promotions?: ApiProductPromotions[];
}

export interface ProductError {
    errorDetail: boolean;
    cause: string;
    message: string;
}

export interface BannerLink {
    id: string;
    datatype?: string;
}

export interface ProductColorRefund {
    catentryId?: number;
    colFilter?: string[];
    customizations?: ProductCustomizations;
    id: string;
    image?: ProductImage;
    isContinuity?: boolean;
    modelHeigh?: string;
    modelName?: string;
    modelSize?: string;
    name: string;
    reference: string;
    sizes?: ProductSizeRefund[];
}
export interface ProductSizeRefund {
    sku: number;
    name: string;
    visibilityValue: string;
    partnumber?: string;
    isBuyable?: boolean;
    backSoon?: string;
    mastersSizeId?: string;
    position?: number;
    price?: string;
    description?: string;
    oldPrice?: string;
    skuDimensions?: ProductSizeSkuDimension[] | null;
    futurePrice?: { price?: string };
    futureVipPrice?: { price?: string };
}
export interface ProductImage {
    timestamp?: string;
    url?: string;
    aux?: string[];
    type?: string[];
    style?: string[];
    availableEstilismo?: boolean;
}

export interface ColorInfo {
    catentryId: number;
    extraInfo?: ExtraInfo;
    id: string;
    image: Image;
    joinLifeInfo?: JoinLife;
    modelHeight?: string;
    modelName?: string;
    modelSize?: string;
    name: string;
    reference: string;
    sizes: SizeInfo[];
}

export interface ExtraInfo {
    fitSizeMessage: FitSizeMessageInfo;
}

export interface FitSizeMessageInfo {
    text: string;
    additionalInfo: FitSizeMessageAdditionalInfo;
}

export interface SizeInfo {
    sku: number;
    name: string;
    partnumber: string;
    isBuyable: boolean;
    backSoon: string;
    mastersSizeId: string;
    position: number;
    price: string;
    description: string;
    oldPrice: string;
    sizeType: string;
    equivalence: string;
    discountsPercentages?: DiscountsPercentages;
}

export interface JoinLife {
    descJoinLife: string;
    isJoinLife: boolean;
}
export interface Image {
    timestamp: string;
    url: string;
    aux: string[];
    type: string[];
    style: string[];
    availableEstilismo: boolean;
}

export interface Composition {
    part: string;
    composition: CompositionItem[];
    name?: string;
}

export interface CompositionItem {
    id: string;
    name: string;
    value: string;
    percentage: string;
}

export interface GenericDetailItem {
    description: string;
    id: string;
    name: string;
}
export interface ProductAttribute {
    id?: string /** Identificador */;
    name?: string /** Nombre */;
    value?: string /** Valor */;
    type?: string /** Tipo de atributo */;
}

export interface ProductDetail {
    familyInfo: ApiProductFamilyInfo;
    subFamilyInfo: ApiProductSubfamilyInfo;
    isJoinLife: boolean;
}

export interface ProductPrice {
    oldPrice?: number /** Precio de la API Ecommerce - precio entero y sin decimales cortado (anterior) para promociones */;
    oldPriceWithCurrencySymbol?: string /** Precio cortado (anterior) para promociones formato y currency */;
    price?: number /** Precio de la API Ecommerce - precio entero y sin decimales */;
    priceWithCurrencySymbol?: string /** Precio con formato y currency */;
    percent?: number /** Porcentaje de descuento aplicado sobre el color */;
    futurePrice?: number /** Precio de la API Ecommerce - precio entero y sin decimales para prewarm */;
    futureVipPrice?: number /** Precio de la API Ecommerce - precio entero y sin decimales para prewarm VIP */;
    futurePriceWithCurrencySymbol?: string /** Precio cortado para prewarm formato y currency */;
    futureVipPriceWithCurrencySymbol?: string /** Precio cortado para prewarm VIP formato y currency */;
    futurePercentage?: number | null /** Porcentaje de descuento futuro */;
    vipPrice?: number;
    oldVipPrice?: number;
    vipPriceWithCurrencySymbol?: string /** Precio vip */;
    originalPrice?: number;
    originalPriceWithCurrencySymbol?: string;
    unitPrice?: ApiUnitPrice;
}

export interface ProductColor {
    catentryId: number;
    customizations?: ProductCustomizations; // Valores de customizacion del producto
    extraInfo?: ProductExtraInfo; /** Mensaje sobre la recomendación de talla */
    id: string /** Identificador */;
    joinLifeInfo?: JoinLifeInfo; /** Info Join Life */
    joinLifeLabelInfo?: JoinLifeLabelInfo; /** Info Join Life */
    medias: Media[] /** Listado de imágenes y videos */;
    modelHeigh?: string;
    modelName?: string;
    modelSize?: string;
    name: string /** nombre */;
    reference?: string;
    sizes?: ProductSize[] /** Listado de tallas */;
    sustainability?: ProductSustainability;
    traceability?: ProductTraceability;
}

export interface ProductCustomizations {
    customization: ProductCustomization;
    customizationDetails: ProductCustomizationDetails;
}

export interface ProductCustomization {
    id: number;
    name: string;
    masterCustomizationType: string;
}

export interface ProductCustomizationDetails {
    storeId: number;
    isGlobal: boolean;
    behaviour: string;
    price: string;
    priceComb?: string;
    areas: ProductCustomizationArea[];
    minAreas?: number;
    maxAreas?: number;
    colors: ProductCustomizationColor[];
    typographies: CustomizationTypography[];
    regularExpression: string;
    regexByAppId: any[] /** Actualmente sin uso en la app. Tipar cuando se le de el primer uso. */;
    optional: boolean;
    dots: boolean;
    customizationAttrValExclusion: any[] /** Actualmente sin uso en la app. Tipar cuando se le de el primer uso. */;
    customizationAttr: any[] /** Actualmente sin uso en la app. Tipar cuando se le de el primer uso. */;
    colorBlocks: any[] /** Actualmente sin uso en la app. Tipar cuando se le de el primer uso. */;
    pathToConfig: any[] /** Actualmente sin uso en la app. Tipar cuando se le de el primer uso. */;
    attrValGroups: any[] /** Actualmente sin uso en la app. Tipar cuando se le de el primer uso. */;
}

export interface ProductCustomizationArea {
    area: CustomizationArea;
    media: ProductCustomizationMedia;
    textMaxLength: number;
}

export interface CustomizationArea {
    description: string;
    id: number;
    lettercase: string;
    typographies: CustomizationTypography[];
}

export interface ProductCustomizationMedia {
    customizableInfo: MediaCustomizableInfo;
    id: string;
}

export interface MediaCustomizableInfo {
    align: string;
    attribute: string;
    height: number;
    left: number;
    rawData: MediaCustomizableInfoRawData;
    top: number;
    viewport: string;
    width: number;
}

export interface MediaCustomizableInfoRawData {
    align: string;
    height: number;
    left: number;
    referenceHeight: number;
    referenceWidth: number;
    top: number;
    width: number;
}

export interface ProductCustomizationColor {
    description: string;
    hexValue: string;
    id: number;
    name: string;
    selectorIcon: string;
    shadowed: boolean;
}

export interface CustomizationTypography {
    description: string;
    descriptions: string[];
    font: string;
    id: number;
    lettercase: string;
    name: string;
}

export interface JoinLifeInfo {
    descJoinLife: string;
    isJoinLife: boolean;
    joinLifeId: string;
    shortDescJoinLife: string;
}

export interface JoinLifeLabelInfo {
    show: boolean;
    areas: Area[];
}

export interface Area {
    key: string;
    description: string;
    parts: Part[];
}

export interface Part {
    key: string;
    description: string;
    materials: Material[];
}

export interface Material {
    key: string;
    percentage: string;
    title: string;
    titleUpper: string;
    description: string;
}

export interface ProductSustainability {
    show?: boolean;
    syntheticFiberPercentage: {
        name: string;
        percentageHigher: boolean;
        value: number;
    };
}

export interface ProductExtraInfo {
    fitSizeMessage: FitSizeMessageInfo;
}

export interface FitSizeMessageAdditionalInfo {
    percentages: FitSizeMessagePercentages;
}

export interface FitSizeMessagePercentages {
    bigComments: number;
    smallComments: number;
}

export interface ProductSize {
    sku: number /** Id de producto unico */;
    name?: string;
    partnumber?: string;
    isBuyable?: boolean;
    backSoon?: string | null;
    mastersSizeId?: string | null;
    priceInfo?: ProductPrice;
    promotionId?: number;
    description?: string | null;
    visibilityValue: ProductAvailabilityType | string; // TODO ECOMDUTI-20992 CONSULTAR string no hace falta
    skuDimensions?: ProductSizeSkuDimension[] | null;
    position?: number;
    price?: string;
    oldPrice?: string | null;
    hasSelected?: boolean;
    stockThreshold?: ProductStockThreshold;
    discountsPercentages?: DiscountsPercentages;
}

export interface ProductSizeSkuDimension {
    dimensionId: string;
    dimensionName: string;
    value: number;
}

export interface ProductTraceability {
    show?: boolean;
    weaving?: ProductTraceabilityItem;
    dyeingPrinting?: ProductTraceabilityItem;
    confection?: ProductTraceabilityItem;
    assembly?: ProductTraceabilityItem;
    pricking?: ProductTraceabilityItem;
    finish?: ProductTraceabilityItem;
}

export interface ProductTraceabilityItem {
    name: string;
    country: string[];
}

export interface Block {
    id: string;
    productIds: null[] | number;
}

export interface Customization {
    datatype: string;
    text: string;
    areaId: number;
    colorId: number;
    typographyId: number;
}

export const lowThreshold: ProductStockThreshold = 'DUTTI_UMBRAL_BAJO';

// eslint-disable-next-line no-secrets/no-secrets
export type ProductStockThreshold = 'DUTTI_UMBRAL_ALTO' | 'DUTTI_UMBRAL_BAJO' | 'DUTTI_UMBRAL_MEDIO' | 'UNDEFINED_UMBRAL_STOCK';
export type ProductType = 'BundleBean' | 'ProductBean' | 'VirtualGiftCard';
export type ProductGridElementType = 'BANNER' | 'BANNER2' | 'BUNDLE' | 'ESTILISMO' | 'GRUCACO' | 'LOOKBOOK' | 'MOCACO' | 'PRODUCT';
export type ProductAvailabilityType = 'BACK_SOON' | 'COMING_SOON' | 'DISABLED' | 'HIDDEN' | 'SHOW' | 'SOLD_OUT';
export enum ProductCopositionType {
    'CLOTHING',
    'FOOTWEAR',
}

export interface GridElement {
    template: string;
    ccIds: number[];
    hiddenFieldsByProduct: Array<{ name: string; productId: string[] }>;
    id: string;
    type: string;
}

export interface DiscountsPercentages {
    originalPriceDiscount?: string; // El % de price vs originalPrice
    oldPriceDiscount?: string; // El % de price vs oldPrice
    originalFuturePriceDiscount?: string; // El % de future price vs originalPrice
    oldFuturePriceDiscount?: string; // El % de futureprice vs price: esto para el caso de promos futuras
}
